
import React from 'react';
import ReactGA from 'react-ga4';

import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Base64 } from 'js-base64';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/free-solid-svg-icons';

import {
  getDateCheckInOutISOString,
  getDateOnly,
  GetDisableNewTabOpen,
  GetMultipleCitiesEnabled,
  GetMultipleQuotesEnabled,
  ICertificateRouteHint,
  RootState,
  scrollTop,
  UrlUtils,
} from '@share/utils';
import { roomsSearchActions, getRoomsDetails, getHotelDetails } from '@store/slices';
import { compareActions, datesActions, locationActions, roomsActions } from '@share/store/slices';
import { D_UPDATE_BUTTON, ROOMS_LABEL, Routes } from '@share/constants';
import { IGuest, ISessionKey, IShareCompareState } from '@share/common-types';
import { DatePickerComponent } from '@share/components';
import { RoomPickerComponent } from '@share/components';
import { getLocationByCode } from '@share/services';
import { DATES_LABEL } from '@constants';
import { BlueButton } from '@share/components';
import { IHotelInfo } from '@common-types';

import './style.scss';

interface IProps {
  hotel: IHotelInfo;
  refAnchor: React.RefObject<HTMLDivElement>;
  matches?: boolean;
  isVacationRentals?: boolean;
  isFocused?: boolean;
  isStaticDetail?: boolean;
  certificateRoute?: ICertificateRouteHint;
}

export function RoomsSearchSearch(props: IProps) {

  const roomsSearchStore = useSelector((state: RootState) => state.roomsSearchStore);
  const roomsStore = useSelector((state: RootState) => state.roomsStore);
  const hotelsStore = useSelector((state: RootState) => state.hotelsStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);
  const menuStore = useSelector((state: RootState) => state.navigationMenuStore);
  const myProfileStore = useSelector((state: RootState) => state.myProfileStore);
  const hotelDetailsStore = useSelector((state: RootState) => state.hotelDetailsStore);
  const datesStore = useSelector((state: RootState) => state.datesStore);

  const { hotel: details } = hotelDetailsStore;
  const { rooms, endDate, error, key, startDate, isRoomsLoading } = roomsSearchStore;
  const { account, user } = loginStore;
  const { rooms: roomsStoreRooms } = roomsStore;
  const { startDateSelected, endDateSelected } = datesStore;

  const { isStaticDetail } = props;

  const hasMultipleCities= GetMultipleCitiesEnabled(loginStore);
  const hasMultipleQuotes= GetMultipleQuotesEnabled(loginStore);

  const disableNewTabOpen = GetDisableNewTabOpen(account, menuStore.items);

  const isAshevillePlay = account?.isAshevillePlay;

  const dispatch = useDispatch();

  const intl = useIntl();

  const history = useHistory();

  const location = useLocation();

  const match = useRouteMatch();
  
  const getDetails = async () => {
    const dates = { startDate, endDate };

    const location = await getLocationByCode(props.hotel?.hotelDetails?.id as number);
    dispatch(locationActions.setSelectLocationLabel(location?.data?.name));
    dispatch(locationActions.setSelectLocation(location?.data));
    dispatch(locationActions.applyLocation());

    dispatch(roomsSearchActions.setDates(dates));
    dispatch(roomsSearchActions.setRooms(rooms));

    dispatch(datesActions.setDatesSelected(dates));
    dispatch(datesActions.applyDates());

    dispatch(roomsActions.setRooms(rooms));
    dispatch(roomsActions.applyRooms());

    dispatch(
      getHotelDetails(
        props.hotel?.hotelDetails?.id as number,
        hotelsStore?.sessionKey as ISessionKey,
        undefined,
        undefined,
        isStaticDetail,
        undefined,
        undefined,
        props.certificateRoute,
      ),
    );
  }

  const updateSearch = async () => {
    if(hasMultipleCities && disableNewTabOpen && !hasMultipleQuotes){
      if (JSON.stringify(roomsStoreRooms?.map((r: any) => ({
        adultsCount: r.adultsCount,
        kids: r.kids?.map((k: any) => k.age)
      }))) !== JSON.stringify(rooms?.map((r: any) => ({
        adultsCount: r.adultsCount,
        kids: r.kids?.map((k: any) => k.age)
      })))) {
        dispatch(compareActions.setclearHotelsCompare());
      } else if(getDateOnly(startDateSelected) !== getDateOnly(startDate)){
        dispatch(compareActions.setclearHotelsCompare());
      } else if(getDateOnly(endDateSelected) !== getDateOnly(endDate)){
        dispatch(compareActions.setclearHotelsCompare());
      }
    }

    if (account?.isAshevillePlay && user?.isAshevillePlayAdmin) {
      scrollTop();

      const { startDate, endDate, rooms } = roomsSearchStore;
  
      const datesParam = UrlUtils.getUrl(DATES_LABEL, { startDate, endDate } as Record<string, unknown>);
      const roomsParam = UrlUtils.getUrl(ROOMS_LABEL, rooms);
      
      const search = Base64.encode(`${datesParam}&${roomsParam}`);

      history.push(`/${account.name}${Routes.Login}?redirect=${location.pathname}&search=${search}`);
    } else {
      if (isStaticDetail && (!details?.adjustedPackageGroups?.length || details?.hotelDetails?.id !== props.hotel?.hotelDetails?.id)) {
        getDetails();
      } else {
        dispatch(
          getRoomsDetails(
            props.hotel?.hotelDetails?.id,
            hotelsStore?.sessionKey as ISessionKey,
            undefined,
            props.certificateRoute,
          ),
        );
      }

      ReactGA.event({
        category: loginStore.account?.name as string,
        action: `${D_UPDATE_BUTTON}_${loginStore.account?.name.toUpperCase()}`,
        label: `User clicked update button on detail`,
        nonInteraction: false,
      });
    }
  };

  const setDates = (dates: { startDate: Date; endDate: Date }) => {
    dispatch(
      roomsSearchActions.setDates({
        startDate: getDateCheckInOutISOString(dates.startDate),
        endDate: getDateCheckInOutISOString(dates.endDate),
      }),
    );
  };

  const setKey = (key: string) => {
    dispatch(roomsSearchActions.setKey(key));
  };

  const clearError = () => {
    dispatch(roomsSearchActions.setError(''));
  };

  const addEmptyRoom = () => {
    dispatch(roomsSearchActions.addEmptyRoom());
  };

  const updateAdultsCount = (roomId: string, count: number) => {
    dispatch(roomsSearchActions.updateAdultsCount({ roomId, count }));
  };

  const addKid = (roomId: string, kid: IGuest) => {
    dispatch(roomsSearchActions.addKid({ roomId, kid }));
  };

  const updateKidAge = (roomId: string, kidId: string, age: number) => {
    dispatch(roomsSearchActions.updateKidAge({ roomId, kidId, age }));
  };

  const removeRoom = (roomId: string) => {
    dispatch(roomsSearchActions.removeRoom(roomId));
  };

  const removeKid = (roomId: string, kidId: string) => {
    dispatch(roomsSearchActions.removeKid({ roomId, kidId }));
  };

  return (
    <div className="rooms-search__search">
      <DatePickerComponent
        resetDates={() => {}}
        intl={intl}
        history={history}
        location={location} 
        match={match}
        clearError={clearError}
        datesStore={{
          startDateSelected: startDate,
          endDateSelected: endDate,
          endDate,
          startDate,
          error,
          key,
          isFocused: props.isFocused
        }}
        loginStore={{ ...loginStore }}
        setDatesSelected={setDates as any}
        setKey={setKey}
        isRoomsSearch={props.matches}
        isUpdateSearch={true}
        menuStore={menuStore}
        myProfileStore={myProfileStore}
      />
      <RoomPickerComponent
        updateAdultsCount={updateAdultsCount}
        removeKid={removeKid}
        addKid={addKid}
        addEmptyRoom={addEmptyRoom}
        removeRoom={removeRoom}
        roomsStore={{ 
          rooms,
          error,
          roomsSelected: rooms
        }}
        loginStore={{ ...loginStore }}
        updateKidAge={updateKidAge}
        isUpdateSearch={true}

        setclearHotelsCompare={() => {}}
        compareStore={{} as IShareCompareState}
      />
      <div className="rooms-search__button">
        <BlueButton disabled={!startDate || !endDate || isRoomsLoading} onClick={updateSearch}>
          <FontAwesomeIcon icon={faRotate} />
          <FormattedMessage id="update.search.your.hotel" />
        </BlueButton>
      </div>

      {isAshevillePlay && error && (
        <div className="date-picker__error" title={error} style={{ marginTop: '5px' }}>
          {error}
        </div>)}
    </div>
  );
}
